import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { EmptyState, StatusPill } from "../components/primitives";
import {
  broadcastPreviewUrl, cancelBroadcast, deleteBroadcast, fetchAudienceSize, fetchBroadcast,
  rate, scheduleBroadcast, sendBroadcastTest,
  type BroadcastDetail, type DeliveryStatus,
} from "../lib/api-broadcasts";
import { ammanStamp } from "../lib/dates";
import { useAuth } from "../lib/auth-session";
import { broadcastPill, SetupChecklist, useTopicLabel } from "./BroadcastsPage";

/**
 * One broadcast: the email exactly as frozen, what happened to it, and the
 * three things you do with a draft — test it, schedule it, or throw it away.
 */
export function BroadcastDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [b, setB] = useState<BroadcastDetail | null>(null);
  const [delivery, setDelivery] = useState<DeliveryStatus>();
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<string>("");
  const topic = useTopicLabel();

  const load = useCallback(() => {
    fetchBroadcast(id)
      .then(({ data, meta }) => { setB(data); setDelivery(meta.delivery); })
      .catch((e: Error) => setError(e.message));
  }, [id]);
  useEffect(load, [load]);

  // While it is going out, the numbers move; refresh them every 20 seconds.
  useEffect(() => {
    if (b?.status !== "sending" && b?.status !== "scheduled") return;
    const t = window.setInterval(load, 20_000);
    return () => window.clearInterval(t);
  }, [b?.status, load]);

  if (error) {
    return <EmptyState title="This broadcast could not be loaded" body={error} hint="It may have been deleted." />;
  }
  if (!b) return <p className="bc-loading">Loading…</p>;

  const t = b.totals;
  const sent = t?.sent ?? 0;
  const pill = broadcastPill(b.status);
  const shownCategory = category || b.variants[0]?.category || "";

  return (
    <div className="bc-detail">
      <div className="bc-head">
        <Link to="/broadcasts" className="bc-back"><span aria-hidden>←</span> Broadcasts</Link>
        <div className="bc-title">
          <h1 className="ui-display">{b.name}</h1>
          <StatusPill tone={pill.tone}>{pill.label}</StatusPill>
        </div>
        <p className="bc-sub">
          {b.status === "sent" && b.sent_at && <>Sent {ammanStamp(b.sent_at)} · </>}
          {b.status === "scheduled" && b.scheduled_at && <>Goes out {ammanStamp(b.scheduled_at)} · </>}
          {b.status === "sending" && <>Sending now · </>}
          To followers of <span lang="ar" dir="rtl">{b.categories.map(topic).join("، ")}</span>
        </p>
      </div>

      <SetupChecklist delivery={delivery} />

      {b.status !== "draft" && (
        <dl className="bc-stats">
          <Stat label="Recipients" value={t?.recipients ?? b.recipient_count} />
          <Stat label="Sent" value={sent} note={t?.failed ? `${t.failed.toLocaleString("en-US")} failed` : undefined} />
          <Stat label="Opened" value={t?.opened ?? 0} note={rate(t?.opened, sent)} />
          <Stat label="Clicked" value={t?.clicked ?? 0} note={rate(t?.clicked, sent)} />
          <Stat label="Unsubscribed" value={t?.unsubscribed ?? 0} note={rate(t?.unsubscribed, sent)} />
          <Stat label="Bounced" value={t?.bounced ?? 0} note={rate(t?.bounced, sent)} />
        </dl>
      )}

      <div className="bc-body">
        <section className="bc-preview" aria-label="The email">
          {b.variants.length > 1 && (
            <div className="nl-segmented" role="group" aria-label="Version by topic">
              {b.variants.map((v) => (
                <button key={v.category} type="button" aria-pressed={v.category === shownCategory}
                  onClick={() => setCategory(v.category)}>
                  <span lang="ar" dir="rtl">{topic(v.category)}</span>
                </button>
              ))}
            </div>
          )}
          {/* The frozen email, served with a CSP that allows no scripts, in a
              frame that allows none either. */}
          <iframe
            className="bc-frame"
            title="Broadcast email"
            src={broadcastPreviewUrl(b.id, shownCategory)}
            sandbox=""
          />
        </section>

        <aside className="bc-side">
          {b.status === "draft" && <DraftActions b={b} delivery={delivery} category={shownCategory} onChange={load} />}
          {(b.status === "scheduled" || b.status === "sending") && <CancelAction id={b.id} onChange={load} />}
          {b.links.length > 0 && (
            <section className="bc-links" aria-label="Clicks by link">
              <h2 className="nl-panel-title">Clicks by link</h2>
              <ol>
                {b.links.map((l) => (
                  <li key={l.idx}>
                    <a href={l.url} target="_blank" rel="noopener noreferrer" dir="ltr">{l.url.replace(/^https?:\/\//, "").replace(/\?.*$/, "")}</a>
                    <span className="tabnum">{l.unique_clicks.toLocaleString("en-US")}</span>
                  </li>
                ))}
              </ol>
            </section>
          )}
          {b.error && <p className="bc-error" role="alert">{b.error}</p>}
        </aside>
      </div>
    </div>
  );
}

function Stat({ label, value, note }: { label: string; value: number; note?: string }) {
  return (
    <div className="bc-stat">
      <dt>{label}</dt>
      <dd className="tabnum">{value.toLocaleString("en-US")}</dd>
      {note && <dd className="bc-stat-note">{note}</dd>}
    </div>
  );
}

function DraftActions({ b, delivery, category, onChange }: {
  b: BroadcastDetail; delivery?: DeliveryStatus; category: string; onChange: () => void;
}) {
  const navigate = useNavigate();
  const { session } = useAuth();
  const [to, setTo] = useState(session.me?.email ?? "");
  const [testState, setTestState] = useState<string | null>(null);
  const [when, setWhen] = useState<"now" | "later">("now");
  const [at, setAt] = useState("");
  const [confirming, setConfirming] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const test = async () => {
    setTestState("Sending…");
    try {
      const { data } = await sendBroadcastTest(b.id, to.split(/[,\s]+/).filter(Boolean), category);
      setTestState(`Sent to ${data.sent.join(", ")}`);
    } catch (e) {
      setTestState((e as Error).message);
    }
  };

  // Step one: count today's audience. Step two: send with that exact number,
  // so nobody confirms a figure that changed under them.
  const review = async () => {
    setError(null);
    setBusy(true);
    try {
      const { data } = await fetchAudienceSize(b.categories);
      setConfirming(data.total);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const schedule = async () => {
    if (confirming === null) return;
    setBusy(true);
    setError(null);
    try {
      await scheduleBroadcast(b.id, when === "later" && at ? new Date(at).toISOString() : null, confirming);
      onChange();
    } catch (e) {
      setError((e as Error).message);
      setConfirming(null);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this draft broadcast? The newsletter itself is not affected.")) return;
    await deleteBroadcast(b.id).then(() => navigate("/broadcasts")).catch((e: Error) => setError(e.message));
  };

  return (
    <>
      <section className="bc-action" aria-label="Send a test">
        <h2 className="nl-panel-title">Send a test</h2>
        <div className="bc-inline">
          <input className="nl-input" type="email" value={to} onChange={(e) => setTo(e.target.value)}
            placeholder="you@altibbi.com" aria-label="Test recipients" dir="ltr" />
          <button type="button" className="nl-btn-secondary" onClick={test} disabled={!to.trim()}>Send test</button>
        </div>
        {testState && <p className="bc-hint" role="status">{testState}</p>}
      </section>

      <section className="bc-action" aria-label="Schedule">
        <h2 className="nl-panel-title">Send to subscribers</h2>
        <div className="bc-when" role="radiogroup" aria-label="When">
          <button type="button" className="ui-pill" role="radio" aria-checked={when === "now"} aria-pressed={when === "now"} onClick={() => setWhen("now")}>Now</button>
          <button type="button" className="ui-pill" role="radio" aria-checked={when === "later"} aria-pressed={when === "later"} onClick={() => setWhen("later")}>Later</button>
        </div>
        {when === "later" && (
          <input className="nl-input" type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} aria-label="Send time" />
        )}
        {confirming === null ? (
          <button type="button" className="nl-btn-primary" onClick={review}
            disabled={busy || !delivery?.ready || (when === "later" && !at)}
            title={delivery?.ready ? undefined : "Sending isn't connected yet"}>
            Review and send
          </button>
        ) : (
          <div className="bc-confirm" role="alertdialog" aria-label="Confirm send">
            <p>
              This goes to <strong className="tabnum">{confirming.toLocaleString("en-US")}</strong> people
              {when === "later" && at ? <> on {ammanStamp(new Date(at).toISOString())}</> : <> now</>}.
              It cannot be unsent.
            </p>
            <div className="bc-inline">
              <button type="button" className="nl-btn-secondary" onClick={() => setConfirming(null)} disabled={busy}>Back</button>
              <button type="button" className="nl-btn-primary" onClick={schedule} disabled={busy || confirming === 0}>
                {when === "later" ? "Schedule" : "Send now"}
              </button>
            </div>
          </div>
        )}
        {error && <p className="bc-error" role="alert">{error}</p>}
      </section>

      <button type="button" className="nl-text-btn bc-delete" onClick={remove}>Delete draft</button>
    </>
  );
}

function CancelAction({ id, onChange }: { id: string; onChange: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const cancel = async () => {
    if (!window.confirm("Stop this broadcast? Anyone not yet sent to will not receive it.")) return;
    await cancelBroadcast(id).then(onChange).catch((e: Error) => setError(e.message));
  };
  return (
    <section className="bc-action" aria-label="Cancel">
      <button type="button" className="nl-btn-secondary" onClick={cancel}>Cancel broadcast</button>
      {error && <p className="bc-error" role="alert">{error}</p>}
    </section>
  );
}
